import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import TopSongs from "@/components/TopSongs";
import JudgeApplicationDialog from "@/components/JudgeApplicationDialog";
import { GENRES } from "@/lib/genres";
import { optimizeImg, srcSet } from "@/lib/img";
import { ArrowRight, MusicNote, Microphone, Heart, Lightning, Trophy, CurrencyCircleDollar, Sparkle, Gavel, Headphones, Ticket, Star } from "@phosphor-icons/react";

const HERO_IMG = "https://images.pexels.com/photos/167605/pexels-photo-167605.jpeg";

export default function Landing() {
  const { user } = useAuth();
  const [judgeOpen, setJudgeOpen] = useState(false);

  // Referral tracking: visitors arriving via a shared link (/?ref=CODE)
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (!ref || sessionStorage.getItem("ref_tracked") === ref) return;
    api.post("/referrals/track", { code: ref })
      .then(() => sessionStorage.setItem("ref_tracked", ref))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      {/* HERO */}
      <section className="relative grain overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <img
            src={optimizeImg(HERO_IMG, 1200)}
            srcSet={srcSet(HERO_IMG, [640, 960, 1280, 1600])}
            sizes="(max-width: 640px) 100vw, (max-width: 1280px) 100vw, 1600px"
            alt=""
            width="1600"
            height="900"
            decoding="async"
            fetchpriority="high"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/30 via-[#050505]/80 to-[#050505]"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-12 pb-20 sm:pt-20 sm:pb-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#B4F34A] animate-pulse"></span>
            <span className="text-xs uppercase tracking-widest text-white/70">Hecho en México</span>
          </div>

          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-3xl">
            Tu historia.<br />
            <span className="text-[#FFB800]">Tu canción.</span><br />
            <span className="text-white/60">Tu MusiKa.</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
            Convierte <span className="text-[#FFB800] font-semibold">tu vida en una canción</span> personalizada — corrido tumbado, reggaeton, rock, metal, balada, salsa, cumbia, electrónica o himnos — en minutos.
            <span className="text-[#B4F34A] font-medium"> Solo $49 MXN.</span>
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link
              to={user ? "/create" : "/register"}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-base active:scale-95 transition-all"
              data-testid="hero-cta-create"
            >
              Crear mi canción
              <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="#generos"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium text-base transition-all"
              data-testid="hero-cta-explore"
            >
              Ver géneros
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES STRIP */}
      <section className="border-y border-white/5 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Microphone, label: "Letra única", desc: "Escrita con IA Claude" },
            { icon: MusicNote, label: "Audio HD", desc: "Voz + instrumentos" },
            { icon: Heart, label: "Tu portada", desc: "Tu foto, tu estilo" },
            { icon: Lightning, label: "Listo en minutos", desc: "Sin esperar días" },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFB800]/10 flex items-center justify-center shrink-0">
                <f.icon size={20} weight="duotone" className="text-[#FFB800]" />
              </div>
              <div>
                <p className="font-semibold text-white tracking-normal">{f.label}</p>
                <p className="text-sm text-white/50">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TOP 5 SONGS */}
      <TopSongs />

      {/* CREATOR ROYALTIES SECTION */}
      <section className="relative overflow-hidden border-y border-white/5">
        <div className="absolute inset-0 -z-0 opacity-30">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#B4F34A]/20 blur-[120px]" />
          <div className="absolute bottom-10 right-10 w-72 h-72 rounded-full bg-[#FFB800]/20 blur-[120px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B4F34A]/10 border border-[#B4F34A]/30 mb-5">
              <CurrencyCircleDollar weight="fill" size={14} className="text-[#B4F34A]" />
              <span className="text-xs uppercase tracking-widest text-[#B4F34A] font-bold">Para creadores</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] max-w-3xl mx-auto mb-5">
              Gana dinero como <span className="text-[#FFB800]">creador de contenido</span>
            </h2>
            <p className="text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl mx-auto">
              Si tus canciones se mantienen en el top cada mes, las premiamos agregándolas al repertorio que vendemos a
              <span className="text-[#B4F34A] font-semibold"> restaurantes, bares, cafeterías, consultorios y más</span>.
              Recibe regalías cada 3, 6 o 12 meses.
            </p>
          </div>

          {/* Royalty split visual */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <Stat icon={Sparkle} accent="#B4F34A" big="60%" label="Para los creadores" desc="Se reparte entre las canciones vigentes del repertorio comercial" />
            <Stat icon={Trophy} accent="#FFB800" big="3 meses" label="Renovación de repertorio" desc="Cada trimestre se mantienen las canciones más reproducidas" />
            <Stat icon={CurrencyCircleDollar} accent="#FF4B00" big="3 · 6 · 12" label="Frecuencia de pago" desc="Tú eliges si quieres tus regalías trimestrales, semestrales o anuales" />
          </div>

          <div className="rounded-2xl bg-black/40 border border-white/10 p-5 sm:p-7 mb-8">
            <p className="text-xs uppercase tracking-widest text-white/40 mb-3">Cómo funciona el reparto</p>
            <ol className="space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FFB800]/10 text-[#FFB800] flex items-center justify-center text-xs font-bold shrink-0">1</span><span>Creas tu canción y, si quieres, autorizas su uso comercial desde el panel de la canción.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FFB800]/10 text-[#FFB800] flex items-center justify-center text-xs font-bold shrink-0">2</span><span>Cada mes seleccionamos el TOP de canciones más escuchadas. Si la tuya queda dentro, entra al repertorio comercial.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FFB800]/10 text-[#FFB800] flex items-center justify-center text-xs font-bold shrink-0">3</span><span>De las ventas a los comercios, <span className="text-[#B4F34A] font-semibold">el 60% se reparte entre los creadores</span> del repertorio y el 40% sostiene la operación.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FFB800]/10 text-[#FFB800] flex items-center justify-center text-xs font-bold shrink-0">4</span><span>Cada 3 meses renovamos el repertorio: las canciones más tocadas se quedan. Puedes tener varias canciones vigentes a la vez.</span></li>
              <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#FFB800]/10 text-[#FFB800] flex items-center justify-center text-xs font-bold shrink-0">5</span><span>Te depositamos a tu cuenta bancaria con la frecuencia que elijas (3, 6 o 12 meses).</span></li>
            </ol>
          </div>

          <div className="text-center">
            <Link
              to={user ? "/library" : "/register"}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-bold text-base active:scale-95 transition-all"
              data-testid="creator-royalties-cta"
            >
              {user ? "Autorizar mis canciones" : "Crear mi primera canción"}
              <ArrowRight size={18} weight="bold" />
            </Link>
            <p className="mt-3 text-xs text-white/40">
              <Link to="/terminos" className="underline hover:text-white/70">Ver cláusula completa en Términos</Link>
            </p>
          </div>
        </div>
      </section>

      {/* JUDGE RECRUITMENT */}
      <section className="relative overflow-hidden border-y border-white/5 bg-gradient-to-b from-[#0a0a0a] via-[#0f0a14] to-[#0a0a0a]">
        <div className="absolute inset-0 -z-0 opacity-40 pointer-events-none">
          <div className="absolute top-1/2 left-1/4 w-80 h-80 rounded-full bg-[#FFB800]/15 blur-[140px]" />
          <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full bg-[#B4F34A]/15 blur-[120px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFB800]/10 border border-[#FFB800]/30 mb-5">
                <Gavel weight="fill" size={14} className="text-[#FFB800]" />
                <span className="text-xs uppercase tracking-widest text-[#FFB800] font-bold">Convocatoria abierta</span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05] mb-5">
                ¿Te encanta la música y sabes <span className="text-[#FFB800]">distinguir una buena canción</span>?
              </h2>
              <p className="text-base sm:text-lg text-white/75 leading-relaxed mb-6">
                Postúlate como <span className="text-white font-semibold">Juez Oficial Tu MusiKa</span> y califica con tu voto las
                mejores canciones del mes. Tu oído puede definir quién entra al repertorio comercial que escuchan miles de
                personas en restaurantes, bares y cafés de México.
              </p>

              <div className="space-y-3 mb-7">
                <BenefitRow icon={Star} text="Reconocimiento social — insignia de Juez Oficial en tu perfil" />
                <BenefitRow icon={Ticket} text="PINs gratis para crear tus propias canciones" />
                <BenefitRow icon={Headphones} text="Acceso anticipado a nuevas funciones y géneros" />
                <BenefitRow icon={Trophy} text="Participación en eventos exclusivos del concurso mensual" />
              </div>

              <button
                onClick={() => setJudgeOpen(true)}
                data-testid="judge-cta-open"
                className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#FFB800] hover:bg-[#FFA300] text-black font-bold text-base active:scale-95 transition-all shadow-[0_10px_40px_-10px_rgba(255,184,0,0.5)]"
              >
                Regístrate como juez
                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <p className="mt-3 text-xs text-white/40">
                Es gratis. Si te seleccionamos, te avisamos por WhatsApp.
              </p>
            </div>

            {/* Right: visual card */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-br from-[#FFB800]/20 to-[#B4F34A]/20 rounded-3xl blur-xl opacity-60" />
              <div className="relative rounded-3xl bg-black/60 backdrop-blur border border-white/10 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-white/10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFB800] to-[#FF7A00] flex items-center justify-center shrink-0">
                    <Gavel weight="fill" size={22} className="text-black" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-base">Panel del Juez</p>
                    <p className="text-xs text-white/50">Vota · Califica · Elige el TOP del mes</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <JudgeMockRow rank="01" title="El Sueño del Norte" genre="Corridos" score={92} />
                  <JudgeMockRow rank="02" title="Mi Historia, Mi Cumbia" genre="Cumbia" score={88} />
                  <JudgeMockRow rank="03" title="Bajo la Luna Banda" genre="Banda" score={84} />
                </div>

                <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <p className="text-xs text-white/50">Tu voto se computa mensualmente</p>
                  <span className="text-xs font-bold text-[#B4F34A]">+ recompensas</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <JudgeApplicationDialog open={judgeOpen} onClose={() => setJudgeOpen(false)} />

      {/* GENRES */}
      <section id="generos" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="mb-10">
          <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-3">Elige tu sonido</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {GENRES.length} géneros, una historia: <span className="text-white/50">la tuya.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GENRES.map((g, i) => (
            <Link
              key={g.key}
              to={user ? "/create" : "/register"}
              data-testid={`genre-card-${g.key}`}
              className={`group relative rounded-2xl overflow-hidden bg-[#121212] border border-white/5 hover:border-white/20 transition-all aspect-[4/5] ${i === 0 ? 'sm:col-span-2 lg:col-span-2 lg:row-span-1 aspect-[8/5]' : ''}`}
            >
              <img
                src={optimizeImg(g.image, 500, 60)}
                srcSet={srcSet(g.image, [300, 500, 800])}
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 360px"
                alt={g.name}
                loading="lazy"
                decoding="async"
                width="500"
                height="500"
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 filter-${g.key}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
              <div className="relative h-full flex flex-col justify-end p-5">
                <div className="inline-block w-fit text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded-full mb-3" style={{ backgroundColor: g.accent, color: "#000" }}>
                  Género
                </div>
                <h3 translate="no" className="notranslate font-display text-2xl sm:text-3xl font-bold text-white leading-tight">{g.name}</h3>
                <p className="text-sm text-white/60 mt-1">{g.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW */}
      <section className="bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <p className="text-sm uppercase tracking-widest text-[#B4F34A] mb-3">Cómo funciona</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12">
            En 4 pasos, tu canción.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { n: "01", t: "Cuéntanos tu historia", d: "Escribe en hasta 3,000 caracteres lo que quieres convertir en canción." },
              { n: "02", t: "Aprueba la letra", d: "Nuestra IA te devuelve una letra estructurada (versos, coro, puente). Tú decides." },
              { n: "03", t: "Sube tu foto", d: "Generamos una portada de álbum con filtro según tu género." },
              { n: "04", t: "Paga y descarga", d: "$49 MXN. Recibe tu canción HD con metadatos y portada incrustada." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl bg-[#121212] border border-white/5 p-6">
                <p className="font-display text-5xl font-black text-[#FFB800]/20 leading-none">{s.n}</p>
                <p className="font-display text-xl font-bold mt-2">{s.t}</p>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-20 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-6">
          Tu vida ya es una canción.<br />
          <span className="text-[#FFB800]">Solo falta grabarla.</span>
        </h2>
        <Link
          to={user ? "/create" : "/register"}
          className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-black font-semibold text-base active:scale-95 transition-all"
          data-testid="footer-cta"
        >
          Empezar por $49 MXN
          <ArrowRight size={18} weight="bold" />
        </Link>
      </section>

      {/* B2B PROMOTIONAL CARD */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="relative overflow-hidden rounded-3xl border border-[#B4F34A]/30 bg-gradient-to-br from-[#0f1f10] via-[#0a1410] to-[#050505] p-6 sm:p-10" data-testid="biz-hero-card">
          {/* Decorative blobs */}
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#B4F34A]/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-[#FFB800]/10 blur-3xl pointer-events-none" />

          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#B4F34A]/15 border border-[#B4F34A]/30 mb-5">
              <span className="text-xs uppercase tracking-widest text-[#B4F34A] font-bold">Para tu negocio</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] max-w-3xl">
              ¿Eres dueño de un <span className="text-[#FFB800]">restaurante, bar o cafetería</span> en México o Latinoamérica?
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl">
              <span className="text-[#B4F34A] font-semibold">Evita multas millonarias</span> por transmitir o tocar música con derechos de autor.
              Te ofrecemos la mejor música — la más variada (baladas, cumbias, rancheras, corridos, rock, salsa y más) —
              hecha por talentos mexicanos y latinoamericanos.
              A cambio de una <span className="text-[#FFB800] font-semibold">cuota simbólica mensual</span>,
              tendrás la mejor música sin temor a multas y, de paso, apoyas a nuestros talentos.
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/negocios/info"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[#B4F34A] hover:bg-[#A0E03A] text-black font-semibold text-base active:scale-95 transition-all"
                data-testid="biz-hero-saber-mas"
              >
                Saber más
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/negocios"
                className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium text-base"
                data-testid="biz-hero-registrar"
              >
                Registrar mi negocio
              </Link>
            </div>
            <p className="mt-4 text-xs text-white/40">Planes desde $299 MXN/mes · Cancela cuando quieras</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-sm text-white/40">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-4">
            <Link to="/quienes-somos" className="hover:text-white transition-colors" data-testid="footer-about">Quiénes somos</Link>
            <span className="text-white/20">·</span>
            <Link to="/aviso-de-privacidad" className="hover:text-white transition-colors" data-testid="footer-privacy">Aviso de Privacidad</Link>
            <span className="text-white/20">·</span>
            <Link to="/terminos" className="hover:text-white transition-colors" data-testid="footer-terms">Términos creadores</Link>
            <span className="text-white/20">·</span>
            <Link to="/negocios/terminos" className="hover:text-white transition-colors" data-testid="footer-terms-business">Términos comercios</Link>
            <span className="text-white/20">·</span>
            <Link to="/promotores" className="hover:text-white transition-colors text-[#B4F34A]/80" data-testid="footer-promoters">Quiero ser promotor</Link>
            <span className="text-white/20">·</span>
            <Link to="/negocios/info" className="hover:text-white transition-colors text-[#B4F34A]/80" data-testid="footer-businesses">Para negocios</Link>
            <span className="text-white/20">·</span>
            <Link to="/login" className="hover:text-white/70 transition-colors text-white/30" data-testid="footer-admin-access">Acceso administrador</Link>
          </div>
          <p className="text-xs text-white/30">© 2026 Marketing de Inteligencia Artificial S. de R.L. de C.V.</p>
          <p className="text-xs text-white/30">Av. Américas 1545, Col. Providencia, Guadalajara, Jal., México · Hecho con corazón en México</p>
        </div>
      </footer>
    </div>
  );
}

function Stat({ icon: Icon, accent, big, label, desc }) {
  return (
    <div className="rounded-2xl bg-[#0f0f0f] border border-white/5 p-5 text-center">
      <Icon size={26} weight="duotone" style={{ color: accent }} className="mx-auto mb-2" />
      <p className="font-display text-4xl sm:text-5xl font-black tracking-tight" style={{ color: accent }}>{big}</p>
      <p className="font-semibold text-white mt-1">{label}</p>
      <p className="text-xs text-white/50 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

function BenefitRow({ icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 rounded-full bg-[#FFB800]/10 flex items-center justify-center shrink-0 mt-0.5">
        <Icon weight="fill" size={14} className="text-[#FFB800]" />
      </div>
      <p className="text-sm text-white/85 leading-relaxed pt-1.5">{text}</p>
    </div>
  );
}

function JudgeMockRow({ rank, title, genre, score }) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
      <div className="font-display text-xl font-black text-[#FFB800]/40 w-7 shrink-0">{rank}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white truncate">{title}</p>
        <p className="text-[10px] uppercase tracking-widest text-white/40">{genre}</p>
      </div>
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#B4F34A]/10 border border-[#B4F34A]/30">
        <Star weight="fill" size={11} className="text-[#B4F34A]" />
        <span className="text-xs font-bold text-[#B4F34A]">{score}</span>
      </div>
    </div>
  );
}
